import { useState, useEffect } from "react";

import { toast } from "react-toastify";

import useImpersonationGuard from "../hooks/useImpersonationGuard";
import apiService from "../services/api";

import MarkdownEditor from "./MarkdownEditor";

// Send Email modal - shared by TicketDetail and CustomerDetails.
// Passes `ticketId` to the API when provided so the email is recorded against
// the ticket; omits it when launched from a customer-scoped page.
const SendEmailModal = ({
  isOpen,
  onClose,
  defaultTo = "",
  defaultSubject = "",
  ticketId,
  title = "Send Email to Customer",
}) => {
  const { guardImpersonatedAction, isImpersonating } = useImpersonationGuard();
  const [to, setTo] = useState(defaultTo);
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTo(defaultTo);
      setSubject(defaultSubject);
      setBody("");
    }
  }, [isOpen, defaultTo, defaultSubject]);

  if (!isOpen) return null;

  const handleSend = async () => {
    if (guardImpersonatedAction("Sending support email")) return;

    if (!to || !subject || !body) {
      toast.error("Please fill in all fields");

      return;
    }

    setSending(true);
    try {
      const payload = { to, subject, body };

      if (ticketId) payload.ticketId = ticketId;
      await apiService.sendTicketEmail(payload);
      toast.success("Email sent successfully");
      onClose();
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error(error.message || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-3xl mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">{title}</h2>
          <button
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            onClick={onClose}
          >
            <i className="fas fa-times" />
          </button>
        </div>

        <div className="space-y-4">
          {isImpersonating && (
            <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
              <i className="fas fa-user-shield mt-0.5" />
              <div>
                <p className="font-semibold">
                  Support email is locked during impersonation.
                </p>
                <p>Return to Admin before sending customer communications.</p>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1">To</label>
            <input
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700"
              disabled={isImpersonating}
              placeholder="recipient@example.com"
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Subject</label>
            <input
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700"
              disabled={isImpersonating}
              placeholder="Email subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Message</label>
            <MarkdownEditor
              disabled={isImpersonating}
              minHeightClass="min-h-[180px]"
              placeholder="Type your message here... (Markdown formatting supported)"
              value={body}
              onChange={setBody}
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            disabled={sending}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg flex items-center gap-2"
            disabled={sending || isImpersonating}
            onClick={handleSend}
          >
            {sending ? (
              <>
                <i className="fas fa-spinner fa-spin" />
                Sending...
              </>
            ) : (
              <>
                <i className="fas fa-paper-plane" />
                Send Email
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendEmailModal;
